import clsx from "clsx";
import { Link2, Plus, Shapes, Unlink, X } from "lucide-react";
import { useId } from "react";

import type { App6EchelonCode, EniRole, OdbNode, OdbOrganisation, OdbVocabulary } from "../../api/types";
import { Checkbox, Switch } from "../../components/Checkbox";
import { CardSection, PropRow } from "../../components/panel";
import { Select } from "../../components/Select";
import { Button, IconButton, inputClass, Kbd, KbdHint, Segmented } from "../../components/ui";
import { unitDesignation } from "../../i18n/designation";
import { k, t, tServer } from "../../i18n/i18n";
import { bi, echelonInfo, functionLabel } from "./app6";
import type { UnitInfo } from "./editorModel";
import { ENI_ROLES, eniRoleLabel } from "./eni";
import { nations } from "./nations";
import { cleanNumber, drawableNumber, validNumber } from "./numbering";
import { parseApp6, shortApp6, withM2 } from "./sidc";
import { echelonRefusal, type EchelonRefusal, nodeAtOrNull, type TreeContext, walk, updateAt } from "./tree";
import { UnitSymbol } from "./UnitSymbol";

/** Fiche de l'unité sélectionnée (propriétés en `PropRow`, champs sous leur libellé) : nom, échelon (marques APP-6,
 *  choix impossibles grisés avec leur raison), fonction (palette), qualificatif, QG, force opérationnelle,
 *  numérotation (T ; M qui suit la formation ou imposé), place dans la manœuvre, rôle et code ennemis, matériels.
 *  Grand aperçu tel que la carte le dessine. */

const REFUSAL: Record<Exclude<EchelonRefusal, null>, string> = {
  root: k("Échelon impossible pour la racine de cette nature."),
  parent: k("Échelon égal ou supérieur à celui de sa formation."),
  children: k("Échelon égal ou inférieur à celui d'un de ses subordonnés."),
  side: k("Réservé aux groupements irréguliers ennemis."),
};

const MAX_MATERIELS = 6;

/** Missions proposées au niveau régiment (gabarits de l'iPad) : celles de l'infanterie, sinon celles des blindés. */
function familyText(family: string | null | undefined): string {
  return family === "INF" ? t("Infanterie") : t("Blindées");
}

export function UnitInspector({
  info,
  tree,
  ctx,
  enemy,
  editable,
  vocabulary,
  rootName,
  onChange,
  onApplyTree,
  onOpenPalette,
  onSeal,
  onClose,
}: {
  info: UnitInfo;
  tree: OdbNode;
  ctx: TreeContext;
  enemy: boolean;
  editable: boolean;
  vocabulary: OdbVocabulary;
  rootName: string;
  onChange: (fn: (node: OdbNode) => OdbNode, group?: string) => void;
  onApplyTree: (tree: OdbNode) => void;
  onOpenPalette: () => void;
  onSeal: () => void;
  /** Fiche en tiroir ou en feuille (écran étroit) : bouton de fermeture. */
  onClose?: () => void;
}) {
  const { node, path, echelon } = info;
  const root = path.length === 0;
  // Formation dont la manœuvre place l'unité : son parent (la brigade d'un bataillon de division), pas la racine.
  const formationName = (root ? null : nodeAtOrNull(tree, path.slice(0, -1))?.name) || rootName;
  const id = useId();
  const side = enemy ? "ENI" : "AMI";
  const palette = ctx.palette;
  const echelons = palette.echelons.filter((e) => e.sides.includes(side));
  const parts = parseApp6(node.app6);
  const label = functionLabel(palette, node.fonction, node.app6) ?? (node.specialty ? tServer(node.specialty) : t("Fonction à choisir"));
  const numberOk = validNumber(node.number);
  const higherOk = validNumber(node.higher);
  const placement = info.server?.placement ?? null;
  const qualifiers = palette.qualifiers.filter((q) => q.sides.includes(side));
  const materiels = vocabulary.materiels;
  const listId = `${id}-materiels`;
  const gtiaRank = !enemy && info.rank === "GTIA";

  const setNumber = (field: "number" | "higher", value: string) =>
    onChange((n) => ({ ...n, [field]: value.replace(/\s+/g, "") || null }), `${field}:${info.key}`);

  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-3 px-4 pt-3 pb-3">
        <div className="flex h-[88px] w-[104px] shrink-0 items-center justify-center rounded-lg bg-canvas">
          <UnitSymbol sidc={info.sidc} size={34} left={drawableNumber(info.number)} right={drawableNumber(info.higher.value)} />
        </div>
        <div className="min-w-0 flex-1 pt-1">
          <p className="text-ui leading-snug font-semibold break-words text-ink">{unitDesignation(node.name.trim()) || unitDesignation(info.code) || t("Sans nom")}</p>
          <p className="text-meta leading-snug text-ink-muted">{label}</p>
          <p className="text-meta leading-snug text-ink-muted">{bi(echelonInfo(palette, echelon))}</p>
        </div>
        {onClose && (
          <IconButton size="sm" onClick={onClose} title={t("Fermer la fiche")} aria-label={t("Fermer la fiche")}>
            <X size={16} />
          </IconButton>
        )}
      </div>

      <CardSection title={t("Unité")}>
        <label className="block py-1.5">
          <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Nom")}</span>
          <input
            data-field="name"
            className={inputClass}
            value={node.name}
            readOnly={!editable}
            maxLength={80}
            placeholder={enemy ? (info.code ? unitDesignation(info.code) : t("Nom libre (facultatif)")) : t("Nom de l'unité")}
            aria-invalid={(!enemy || root) && editable && !node.name.trim() ? true : undefined}
            onChange={(e) => onChange((n) => ({ ...n, name: e.target.value }), `name:${info.key}`)}
            onBlur={onSeal}
          />
        </label>
        <div className="py-1.5" data-field="echelon">
          <Segmented
            label={t("Échelon")}
            size="sm"
            options={echelons.map((e) => {
              const refusal = editable ? echelonRefusal(tree, path, ctx, e.code) : null;
              return {
                value: e.code,
                label: e.mark || "—",
                disabled: !editable || (refusal !== null && e.code !== echelon),
                title: refusal ? `${bi(e)} — ${t(REFUSAL[refusal])}` : bi(e),
              };
            })}
            value={echelon}
            onChange={(code: App6EchelonCode) =>
              onChange((n) => ({ ...n, echelon: code, ...(enemy ? { role: n.role ?? null } : {}) }))
            }
          />
          <p className="mt-1 text-meta leading-snug text-ink-muted">{bi(echelonInfo(palette, echelon))}</p>
        </div>
        <div className="py-1.5" data-field="app6">
          <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Fonction APP-6")}</span>
          <button
            type="button"
            disabled={!editable}
            onClick={onOpenPalette}
            className="flex min-h-11 w-full items-center gap-2.5 rounded-lg border border-line-strong bg-surface px-2.5 py-1.5 text-left transition-colors hover:bg-hover disabled:hover:bg-surface"
          >
            <UnitSymbol sidc={info.sidc} size={13} box={36} />
            <span className="min-w-0 flex-1">
              <span className="block text-ui leading-snug break-words text-ink">{label}</span>
              <span className="block font-mono text-caps text-ink-muted tabular-nums">{shortApp6(node.app6)}</span>
            </span>
            {editable && (
              <span className="flex shrink-0 items-center gap-1 text-meta text-ink-muted">
                <Shapes size={14} aria-hidden />{" "}
                <KbdHint className="max-md:hidden">
                  <Kbd>F2</Kbd>
                </KbdHint>
              </span>
            )}
          </button>
        </div>
        {parts && (
          <div className="py-1.5">
            <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Qualificatif")}</span>
            <Select
              className={inputClass}
              value={parts.m2}
              disabled={!editable}
              aria-label={t("Qualificatif")}
              title={t("Modificateur du secteur 2 du symbole (aéroporté, montagne, automoteur…)")}
              onChange={(e) => onChange((n) => ({ ...n, app6: n.app6 ? withM2(n.app6, e.target.value) : n.app6 }))}
            >
              <option value="00">{t("Aucun")}</option>
              {qualifiers.map((q) => (
                <option key={q.m2} value={q.m2}>
                  {bi(q)}
                </option>
              ))}
              {parts.m2 !== "00" && !qualifiers.some((q) => q.m2 === parts.m2) && <option value={parts.m2}>{parts.m2}</option>}
            </Select>
          </div>
        )}
        <div className="flex flex-wrap gap-x-5 gap-y-2 py-2.5">
          <Switch
            checked={info.hq}
            disabled={!editable}
            onChange={(hq) => onChange((n) => ({ ...n, hq }))}
            label={t("Quartier général")}
            title={t("Hampe du symbole : unité de commandement de sa formation")}
          />
          <Switch
            checked={info.tf}
            disabled={!editable}
            onChange={(tf) => onChange((n) => ({ ...n, tf }))}
            label={t("Force opérationnelle")}
            title={t("Accolade au-dessus du symbole : groupement tactique (GTIA, S/GTIA)")}
          />
        </div>
      </CardSection>

      <CardSection title={t("Numérotation")}>
        <label className="block py-1.5">
          <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Numéro de l'unité (bas gauche)")}</span>
          <input
            data-field="number"
            className={clsx(inputClass, "font-mono tabular-nums aria-invalid:border-danger")}
            inputMode="numeric"
            maxLength={8}
            value={node.number ?? ""}
            readOnly={!editable}
            placeholder={t("Ex. 2")}
            aria-invalid={!numberOk || undefined}
            aria-describedby={!numberOk ? `${id}-number` : undefined}
            onChange={(e) => setNumber("number", e.target.value)}
            onBlur={onSeal}
          />
          {!numberOk && (
            <span id={`${id}-number`} className="mt-1 block text-meta leading-snug text-danger">
              {t("Chiffres seulement, quatre au plus.")}
            </span>
          )}
        </label>
        {root ? (
          <label className="block py-1.5">
            <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Numéro de la formation supérieure (bas droite)")}</span>
            <input
              data-field="higher"
              className={clsx(inputClass, "font-mono tabular-nums aria-invalid:border-danger")}
              inputMode="numeric"
              maxLength={8}
              value={node.higher ?? ""}
              readOnly={!editable}
              placeholder={t("Facultatif, ex. 3")}
              aria-invalid={!higherOk || undefined}
              onChange={(e) => setNumber("higher", e.target.value)}
              onBlur={onSeal}
            />
            {!higherOk && <span className="mt-1 block text-meta leading-snug text-danger">{t("Chiffres seulement, quatre au plus.")}</span>}
          </label>
        ) : cleanNumber(node.higher) !== null || node.higher === "" ? (
          <label className="block py-1.5">
            <span className="mb-1 flex items-center justify-between gap-2 text-ui font-medium text-ink-soft">
              {t("Formation supérieure imposée (bas droite)")}
              {editable && (
                <Button variant="link" onClick={() => onChange((n) => ({ ...n, higher: null }))}>
                  <Link2 size={14} /> {t("Suivre la formation")}
                </Button>
              )}
            </span>
            <input
              data-field="higher"
              className={clsx(inputClass, "font-mono tabular-nums aria-invalid:border-danger")}
              inputMode="numeric"
              maxLength={8}
              value={node.higher ?? ""}
              readOnly={!editable}
              aria-invalid={!higherOk || undefined}
              onChange={(e) => onChange((n) => ({ ...n, higher: e.target.value.replace(/\s+/g, "") }), `higher:${info.key}`)}
              onBlur={onSeal}
            />
            {!higherOk && <span className="mt-1 block text-meta leading-snug text-danger">{t("Chiffres seulement, quatre au plus.")}</span>}
          </label>
        ) : (
          <div className="py-1.5" data-field="higher">
            <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Formation supérieure (bas droite)")}</span>
            <div className="flex min-h-8 items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-1.5 text-ui text-ink-muted">
                <Link2 size={14} aria-hidden className="shrink-0" />
                {info.higher.value ? t("Suit la formation ({n})", { n: info.higher.value }) : t("Suit la formation (sans numéro)")}
              </span>
              {editable && (
                <Button size="sm" variant="secondary" onClick={() => onChange((n) => ({ ...n, higher: info.higher.value ?? "" }))}>
                  <Unlink size={14} /> {t("Imposer")}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardSection>

      {(info.placedRank || gtiaRank) && (
        <CardSection title={t("Manœuvre")}>
          {info.placedRank && (
            <div className="py-1.5">
              <Checkbox
                checked={info.manoeuvre}
                disabled={!editable}
                onChange={(manoeuvre) => onChange((n) => ({ ...n, manoeuvre }))}
                label={t("Tient un emplacement de la manœuvre")}
                detail={
                  placement?.slot !== null && placement?.slot !== undefined
                    ? t("Emplacement {n} sur {total} de la manœuvre de « {name} »", { n: placement.slot + 1, total: placement.slots, name: formationName })
                    : info.manoeuvre
                      ? t("Placée par la manœuvre quand sa formation est commandée.")
                      : t("Non placée : présente dans l'ordre de bataille, sans emplacement.")
                }
              />
            </div>
          )}
          {gtiaRank && <PropRow label={t("Missions au niveau régiment")}>{familyText(info.entry?.family)}</PropRow>}
        </CardSection>
      )}

      {root && !enemy && (
        <CardSection title={t("Formation")}>
          <div className="py-1.5" data-field="nation">
            <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Nation")}</span>
            <Select
              className={inputClass}
              value={node.nation ?? ""}
              disabled={!editable}
              placeholder={t("Facultative")}
              aria-label={t("Nation")}
              onChange={(e) => onChange((n) => ({ ...n, nation: e.target.value || null }))}
            >
              <option value="">{t("— Non précisée —")}</option>
              {nations().map((n) => (
                <option key={n.code} value={n.code}>
                  {n.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="py-1.5">
            <Segmented
              label={t("Organisation")}
              size="sm"
              options={[
                { value: "gtia", label: t("Groupements tactiques"), title: t("GTIA et S/GTIA : cadre force opérationnelle par défaut") },
                { value: "organique", label: t("Unités organiques"), title: t("Bataillons et compagnies, sans cadre force opérationnelle") },
              ]}
              value={node.organisation ?? "gtia"}
              onChange={(organisation: OdbOrganisation) => {
                if (!editable) return;
                // Le cadre force opérationnelle de chaque unité revient au défaut de la nouvelle organisation.
                let next = updateAt(tree, [], (n) => ({ ...n, organisation }));
                walk(next, (_, p) => {
                  next = updateAt(next, p, (n) => ({ ...n, tf: null }));
                });
                onApplyTree(next);
              }}
            />
          </div>
        </CardSection>
      )}

      {enemy && (
        <CardSection title={t("Ennemi")}>
          <div className="py-1.5" data-field="role">
            <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Rôle")}</span>
            <Select
              className={inputClass}
              value={node.role ?? ""}
              disabled={!editable}
              aria-label={t("Rôle de l'unité")}
              title={t("Rôle : emplacement de l'unité dans la manœuvre et son échelon dans l'ordre")}
              onChange={(e) => onChange((n) => ({ ...n, role: (e.target.value || null) as EniRole | null }))}
            >
              <option value="">
                {info.role && !node.role ? t("Celui de sa nature ({role})", { role: eniRoleLabel(info.role) }) : t("— Celui de sa nature —")}
              </option>
              {ENI_ROLES.map((role) => (
                <option key={role} value={role}>
                  {eniRoleLabel(role)}
                </option>
              ))}
            </Select>
          </div>
          <PropRow label={t("Code cité par l'ordre")} mono>
            {info.code ? unitDesignation(info.code) : "…"}
          </PropRow>
        </CardSection>
      )}

      <CardSection title={t("Matériels")}>
        <datalist id={listId}>
          {materiels.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        <div className="space-y-1.5 py-1.5" data-field="materiel">
          {node.materiel.map((m, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                className={inputClass}
                list={listId}
                value={m}
                readOnly={!editable}
                maxLength={60}
                aria-label={i === 0 ? t("Matériel majeur") : t("Matériel {n}", { n: i + 1 })}
                onChange={(e) =>
                  onChange((n) => ({ ...n, materiel: n.materiel.map((x, j) => (j === i ? e.target.value : x)) }), `materiel:${info.key}:${i}`)
                }
                onBlur={onSeal}
              />
              {editable && (
                <IconButton
                  size="sm"
                  title={t("Retirer ce matériel")}
                  aria-label={t("Retirer ce matériel")}
                  onClick={() => onChange((n) => ({ ...n, materiel: n.materiel.filter((_, j) => j !== i) }))}
                >
                  <X size={14} />
                </IconButton>
              )}
            </div>
          ))}
          {editable && node.materiel.length < MAX_MATERIELS && (
            <Button size="sm" variant="secondary" onClick={() => onChange((n) => ({ ...n, materiel: [...n.materiel, ""] }))}>
              <Plus size={14} /> {node.materiel.length ? t("Ajouter un matériel") : t("Ajouter un matériel majeur")}
            </Button>
          )}
          {!editable && node.materiel.length === 0 && <p className="text-meta text-ink-muted">{t("Aucun matériel.")}</p>}
        </div>
        <label className="block py-1.5">
          <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Nombre de matériels majeurs")}</span>
          <input
            data-field="quantity"
            type="number"
            min={0}
            max={100000}
            className={clsx(inputClass, "tabular-nums")}
            value={node.quantity ?? ""}
            readOnly={!editable}
            title={t("Nombre de matériels majeurs (logistique : UE et UF)")}
            onChange={(e) =>
              onChange(
                (n) => ({ ...n, quantity: e.target.value === "" ? null : Math.max(0, Math.min(100000, Number(e.target.value))) }),
                `quantity:${info.key}`,
              )
            }
            onBlur={onSeal}
          />
        </label>
      </CardSection>
      {info.issues.length > 0 && (
        <CardSection title={t("Points à vérifier")}>
          <ul className="space-y-1 py-1">
            {info.issues.map((issue, i) => (
              <li key={i} className={clsx("text-meta leading-snug", issue.level === "error" ? "text-danger" : issue.level === "warning" ? "text-warn" : "text-ink-muted")}>
                {tServer(issue.message)}
              </li>
            ))}
          </ul>
        </CardSection>
      )}
    </div>
  );
}

